import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'ad-sensory';
const SCALES = [1, 1.125, 1.25];

// Initial values mirror the inline script in public/index.html, which has
// already applied them to <html> before React mounted.
const readInitial = () => {
  const root = document.documentElement;
  return {
    theme: root.dataset.theme || 'dark',
    motion: root.dataset.motion || 'full',
    font: root.dataset.font || 'default',
    calm: root.dataset.calm === 'on',
    scale: Number(getComputedStyle(root).getPropertyValue('--scale')) || 1,
  };
};

const SensoryContext = createContext(null);

export const SensoryProvider = ({ children }) => {
  const [settings, setSettings] = useState(readInitial);

  // Layout effect so <html> is updated before children read CSS variables in their effects.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = settings.theme;
    root.dataset.motion = settings.motion;
    root.dataset.font = settings.font;
    root.dataset.calm = settings.calm ? 'on' : 'off';
    root.style.setProperty('--scale', settings.scale);

    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', settings.theme === 'light' ? '#f6f5f1' : '#0b0c0f');

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      // Storage can be unavailable (private mode); settings still apply for this visit.
    }
  }, [settings]);

  const update = useCallback((patch) => setSettings((prev) => ({ ...prev, ...patch })), []);

  const reset = useCallback(() => {
    const mq = (q) => window.matchMedia && window.matchMedia(q).matches;
    setSettings({
      theme: mq('(prefers-color-scheme: light)') ? 'light' : 'dark',
      motion: mq('(prefers-reduced-motion: reduce)') ? 'reduce' : 'full',
      font: 'default',
      calm: false,
      scale: 1,
    });
  }, []);

  const value = useMemo(
    () => ({ settings, update, reset, scales: SCALES, reducedMotion: settings.motion === 'reduce' }),
    [settings, update, reset]
  );

  return <SensoryContext.Provider value={value}>{children}</SensoryContext.Provider>;
};

export const useSensory = () => {
  const ctx = useContext(SensoryContext);
  if (!ctx) throw new Error('useSensory must be used inside <SensoryProvider>');
  return ctx;
};
